/**
 * ConfirmDialog/index.tsx
 * desc: the confirmation dialog — a question with OK / Cancel buttons over a dimmed page, centered or anchored beside
 *       the control that asked. confirm() (confirm.ts) opens it from anywhere; this host, mounted once at the router
 *       root (AppRouter), renders whatever question is open. Looks like the toastr confirm it replaces
 * note: both faces are modal alert dialogs: focus is trapped (OK takes it first), the page is blocked, a click
 *       outside does nothing and Escape cancels. A question outlives nothing: it closes when the user navigates
 *       away or when its anchor leaves the document (polling, a parent re-render), instead of hanging over content
 *       that no longer asked it. The anchored face is the popover primitive only because the dialog primitive cannot
 *       be positioned. Neither face has a trigger for radix to return focus to, so that is done here
 */

import { useEffect, useId, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router';
import * as DialogRadix from '@radix-ui/react-dialog';
import * as PopoverRadix from '@radix-ui/react-popover';
import { translate } from '../../utilities/localization';
import { getSnapshot, settle, subscribe } from './confirm';
import type { ConfirmPrompt } from './confirm';
import './styles.css';

export { confirm } from './confirm';
export type { ConfirmOptions } from './confirm';

type PromptProps = { prompt: ConfirmPrompt };

const _handleOpenChange = (isOpen: boolean) => { if (!isOpen) settle(false); };

const _handleInteractOutside = (event: Event) => { event.preventDefault(); };

const _handleConfirmClick = () => { settle(true); };

const _handleCancelClick = () => { settle(false); };

const _restoreFocus = (prompt: ConfirmPrompt) => (event: Event) => {
    event.preventDefault();
    prompt.returnFocus?.focus();
};

const Buttons = ({ prompt }: PromptProps) => (
    <div className="buttons">
        <button type="button" onClick={ _handleConfirmClick }>{ prompt.confirmLabel ?? translate('common.action.ok') }</button>
        <button type="button" onClick={ _handleCancelClick }>{ prompt.cancelLabel ?? translate('common.action.cancel') }</button>
    </div>
);

// the question is the dialog's title and there is no description — radix warns unless told so explicitly
const CenteredPrompt = ({ prompt }: PromptProps) => (
    <DialogRadix.Root open={ true } onOpenChange={ _handleOpenChange }>
        <DialogRadix.Portal>
            <DialogRadix.Overlay className="component-confirm-dialog-overlay" />
            <DialogRadix.Content
                aria-describedby={ undefined }
                className="component-confirm-dialog centered"
                onCloseAutoFocus={ _restoreFocus(prompt) }
                onInteractOutside={ _handleInteractOutside }
                role="alertdialog"
            >
                <DialogRadix.Title asChild><p className="message">{ prompt.text }</p></DialogRadix.Title>
                <Buttons prompt={ prompt } />
            </DialogRadix.Content>
        </DialogRadix.Portal>
    </DialogRadix.Root>
);

// the popover primitive has no overlay of its own, and its portal takes a single child
const AnchoredPrompt = ({ anchor, prompt }: PromptProps & { anchor: HTMLElement }) => {
    const messageId = useId();

    // nothing tells the host that the anchor unmounted (polling, a parent re-render, navigation): it only re-renders
    // on store changes, and the popover would keep measuring a detached node and jump to the top-left corner. So
    // watch the document and close the question the moment its anchor is gone
    useEffect(() => {
        const observer = new MutationObserver(() => { if (!anchor.isConnected) settle(false); });
        observer.observe(document.body, { childList: true, subtree: true });
        return () => { observer.disconnect(); };
    }, [ anchor ]);

    return <>
        { createPortal(<div className="component-confirm-dialog-overlay" />, document.body) }
        <PopoverRadix.Root modal={ true } open={ true } onOpenChange={ _handleOpenChange }>
            <PopoverRadix.Anchor virtualRef={{ current: anchor }} />
            <PopoverRadix.Portal>
                <PopoverRadix.Content
                    align="end"
                    aria-labelledby={ messageId }
                    className="component-confirm-dialog"
                    onCloseAutoFocus={ _restoreFocus(prompt) }
                    onInteractOutside={ _handleInteractOutside }
                    role="alertdialog"
                    side="bottom"
                    sideOffset={ 6 }
                >
                    <p className="message" id={ messageId }>{ prompt.text }</p>
                    <Buttons prompt={ prompt } />
                </PopoverRadix.Content>
            </PopoverRadix.Portal>
        </PopoverRadix.Root>
    </>;
};

const ConfirmDialog = () => {
    const prompt = useSyncExternalStore(subscribe, getSnapshot);
    const location = useLocation();

    // leaving a page takes the control that asked with it: close the question rather than leave it over the next page
    useEffect(() => () => { settle(false); }, [ location.key ]);

    if (!prompt) return null;
    return prompt.anchor ? <AnchoredPrompt anchor={ prompt.anchor } prompt={ prompt } /> : <CenteredPrompt prompt={ prompt } />;
};


export default ConfirmDialog;
